import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';
import AgentTierMatrix from './ui/AgentTierMatrix';

const { FiArrowLeft, FiArrowRight, FiCheck, FiX, FiUploadCloud } = FiIcons;

const rivals = [
  {
    name: 'Scrivener',
    good: 'A powerful, beloved organizer for long manuscripts — corkboard, binder, compile.',
    gap: 'No AI, no voice matching, and KDP formatting is a manual compile you have to learn.',
    add: 'Keeps the long-form organization, adds Author Voice DNA, AI drafting, and live KDP Preflight.'
  },
  {
    name: 'Sudowrite',
    good: 'Genuinely fun AI brainstorming and "describe/expand" tools for fiction.',
    gap: 'One blended "AI voice," no real KDP/export workflow, no publishing path.',
    add: 'AI that writes in YOUR voice (not a house style), plus preflight + clean KDP export.'
  },
  {
    name: 'Atticus',
    good: 'Clean formatting and export to EPUB/print — a solid Vellum alternative.',
    gap: 'Formatting-only: no AI writing, no voice, no live compliance while you draft.',
    add: 'Everything Atticus exports, but you also write the book here — in your voice, KDP-checked as you go.'
  },
  {
    name: 'Reedsy',
    good: 'A nice free editor plus a marketplace of freelance editors and designers.',
    gap: 'No AI drafting, no voice model, and real help means hiring (and paying) a pro.',
    add: 'Built-in AI in your voice + co-author/editor collaboration in the same doc — no marketplace required.'
  },
  {
    name: 'Generic AI (ChatGPT)',
    good: 'Endlessly flexible and cheap for quick drafting and ideas.',
    gap: 'Sounds like everyone else, forgets your book between chats, and knows nothing about KDP.',
    add: 'A real book editor with chapters + memory, AI grounded in your Voice DNA, and KDP-ready export.'
  }
];

const vendors = ['ManuscriptSmith', 'Scrivener', 'Sudowrite', 'Atticus', 'Reedsy', 'ChatGPT'];
const rows = [
  ['Writes in YOUR voice (Voice DNA)', [true, false, 'Generic', false, false, 'Generic']],
  ['AI drafting / rewrite / outline', [true, false, true, false, false, true]],
  ['Long-form book editor', [true, true, false, 'Format only', true, false]],
  ['Live KDP Preflight + fixes', [true, false, false, 'Format only', false, false]],
  ['One-click EPUB / PDF / KDP export', [true, 'Compile', false, true, true, false]],
  ['Co-authoring + editor comments', [true, 'Limited', false, false, true, false]],
  ['Social post drafting + review queue', [true, false, false, false, false, false]],
  ['Embeddable reader for your site', [true, false, false, false, false, false]],
  ['Audiobook add-on', [true, false, false, false, false, false]]
];

const Mark = ({ v }) => {
  if (v === true) return <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />;
  if (v === false) return <SafeIcon icon={FiX} className="w-4 h-4 text-gray-300 mx-auto" />;
  return <span className="text-[11px] text-gray-500">{v}</span>;
};

const ComparePage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'ManuscriptSmith vs Scrivener, Sudowrite, Atticus & Reedsy',
    description: 'How ManuscriptSmith compares to Scrivener, Sudowrite, Atticus, Reedsy, and generic AI for writing and self-publishing a book.',
    url: 'https://manuscriptsmith.com/compare'
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="ManuscriptSmith vs Scrivener, Sudowrite, Atticus & Reedsy"
        description="A clear, honest comparison: how ManuscriptSmith stacks up against Scrivener, Sudowrite, Atticus, Reedsy, and ChatGPT for writing in your voice and shipping a KDP-ready book."
        keywords="scrivener alternative, sudowrite alternative, atticus alternative, reedsy alternative, best ai book writing software, write a book ai comparison"
        canonicalUrl="https://manuscriptsmith.com/compare"
        structuredData={structuredData}
      />

      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why authors <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">switch</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Most tools do one piece — organizing, or AI ideas, or formatting, or editors-for-hire. ManuscriptSmith
            does the whole arc: write in your voice, stay KDP-ready, publish, and sell. Here's the honest breakdown.
          </p>
        </section>

        {/* Per-rival cards */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="grid md:grid-cols-2 gap-6">
            {rivals.map((r, i) => (
              <motion.div key={r.name} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}>
                <h2 className="text-xl font-bold text-gray-900 mb-3">ManuscriptSmith <span className="text-gray-400 font-normal">vs</span> {r.name}</h2>
                <div className="space-y-2.5 text-sm">
                  <p className="flex gap-2"><SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /><span className="text-gray-600"><strong className="text-gray-800">{r.name} is great for:</strong> {r.good}</span></p>
                  <p className="flex gap-2"><SafeIcon icon={FiX} className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" /><span className="text-gray-600"><strong className="text-gray-800">Where it stops:</strong> {r.gap}</span></p>
                  <p className="flex gap-2 bg-primary-50 rounded-lg p-2"><SafeIcon icon={FiArrowRight} className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" /><span className="text-primary-900"><strong>ManuscriptSmith adds:</strong> {r.add}</span></p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Matrix */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">The whole picture, side by side</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[760px]">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-3 text-gray-700">Capability</th>
                    {vendors.map((v, i) => (
                      <th key={v} className={`py-3 px-3 text-sm ${i === 0 ? 'text-primary-600 font-bold' : 'text-gray-500 font-medium'}`}>{v}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r[0]} className="border-b border-gray-100">
                      <td className="py-3 px-3 text-sm text-gray-800 font-medium">{r[0]}</td>
                      {r[1].map((val, j) => (
                        <td key={j} className={`py-3 px-3 text-center ${j === 0 ? 'bg-primary-50/40' : ''}`}><Mark v={val} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">Reflects typical positioning of each tool; features evolve. Not affiliated with the products named.</p>
          </div>
        </section>

        {/* Inside ManuscriptSmith — data-driven agent x plan matrix from the registry */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Inside ManuscriptSmith — every agent, by plan</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The other tools give you one feature. ManuscriptSmith is a studio of agents. Here&apos;s exactly which one is in each plan.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8">
              <AgentTierMatrix />
            </div>
            <p className="text-center text-sm text-gray-500 mt-6">
              See the details for each agent on the <Link to="/capabilities" className="text-primary-600 font-semibold hover:underline">capabilities page</Link>.
            </p>
          </div>
        </section>

        {/* Switch in minutes */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="bg-parchment-100 border border-parchment-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-5">
            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiUploadCloud} className="w-7 h-7 text-white" />
            </div>
            <p className="text-gray-700 text-center sm:text-left">
              <strong>Already started elsewhere?</strong> Import your draft as DOCX, EPUB, TXT, or PDF, paste a sample to
              build your Voice Memory, and keep writing — your chapters and formatting come with you.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">See the difference in your own voice</h2>
            <p className="text-lg opacity-90 mb-8">Play with the live editor demo first.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/editor" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                Try the live editor
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ComparePage;
